import { useMemo } from 'react';
import type { ViewTechnician } from '../api/viewTechnicianApiService';

// ============================================================================
// Constants
// ============================================================================

export const ROLE_LABEL_TECHNICIAN = 'Technik';

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Formats an ISO date string to Polish locale format
 * Format: YYYY/MM/DD, HH:MM:SS
 */
export const formatCreatedAt = (
  isoString: string | null | undefined
): string => {
  if (!isoString) {
    return '—';
  }
  const date = new Date(isoString);
  const formatter = new Intl.DateTimeFormat('pl-PL', {
    timeZone: 'Europe/Warsaw',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(date);
  const year = parts.find((p) => p.type === 'year')?.value;
  const month = parts.find((p) => p.type === 'month')?.value;
  const day = parts.find((p) => p.type === 'day')?.value;
  const hour = parts.find((p) => p.type === 'hour')?.value;
  const minute = parts.find((p) => p.type === 'minute')?.value;
  const second = parts.find((p) => p.type === 'second')?.value;
  return `${year}/${month}/${day}, ${hour}:${minute}:${second}`;
};

export const orDash = (value: string | null | undefined): string => {
  return value?.trim() ? value : '—';
};

export const formatPhoneNumber = (
  countryCode: string | null | undefined,
  phoneNumber: string | null | undefined
): string => {
  const parts = [countryCode, phoneNumber].filter(Boolean);
  return parts.join(' ').trim() || '—';
};

export interface UseViewTechnicianContentReturn {
  formattedTechnicianId: string;
  formattedName: string;
  formattedEmail: string;
  formattedPhoneNumber: string;
  formattedRole: string;
  formattedStatus: string;
  formattedCreatedAt: string;
  formattedLastLoggedIn: string;
  formattedAddressLine1: string;
  formattedAddressLine2: string;
  formattedCity: string;
  formattedState: string;
  formattedZipCode: string;
  formattedCountry: string;
}

export const useViewTechnicianContent = (
  technician: ViewTechnician | null
): UseViewTechnicianContentReturn => {
  const formattedTechnicianId = useMemo(
    () => orDash(technician?.technician_id),
    [technician?.technician_id]
  );

  const formattedName = useMemo(
    () => orDash(technician?.name),
    [technician?.name]
  );

  const formattedEmail = useMemo(
    () => orDash(technician?.email),
    [technician?.email]
  );

  const formattedPhoneNumber = useMemo(
    () => formatPhoneNumber(technician?.country_code, technician?.phone_number),
    [technician?.country_code, technician?.phone_number]
  );

  const formattedRole = useMemo(
    () => (technician?.role ? ROLE_LABEL_TECHNICIAN : '—'),
    [technician?.role]
  );

  const formattedStatus = useMemo(() => {
    if (technician?.status === 'active') {
      return 'Aktywny';
    }
    if (technician?.status === 'inactive') {
      return 'Nieaktywny';
    }
    return '—';
  }, [technician?.status]);

  const formattedCreatedAt = useMemo(
    () => formatCreatedAt(technician?.created_at),
    [technician?.created_at]
  );

  const formattedLastLoggedIn = useMemo(
    () => formatCreatedAt(technician?.last_logged_in ?? undefined),
    [technician?.last_logged_in]
  );

  const formattedAddressLine1 = useMemo(
    () => orDash(technician?.address_line1),
    [technician?.address_line1]
  );

  const formattedAddressLine2 = useMemo(
    () => orDash(technician?.address_line2),
    [technician?.address_line2]
  );

  const formattedCity = useMemo(
    () => orDash(technician?.city),
    [technician?.city]
  );

  const formattedState = useMemo(
    () => orDash(technician?.state),
    [technician?.state]
  );

  const formattedZipCode = useMemo(
    () => orDash(technician?.zip_code),
    [technician?.zip_code]
  );

  const formattedCountry = useMemo(
    () => orDash(technician?.country),
    [technician?.country]
  );

  return {
    formattedTechnicianId,
    formattedName,
    formattedEmail,
    formattedPhoneNumber,
    formattedRole,
    formattedStatus,
    formattedCreatedAt,
    formattedLastLoggedIn,
    formattedAddressLine1,
    formattedAddressLine2,
    formattedCity,
    formattedState,
    formattedZipCode,
    formattedCountry,
  };
};
